/**
 * @module experience/multiline-msisdn-dropdown
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */

// Core Libraries
import { Component, OnInit, OnDestroy, EventEmitter, Output, Input } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// interfaces
import {
  LineSummary,
  CustomerAccountBillingDetails,
} from '@support/api/proxies/responses/api-2006.interface';
import { SelectItem } from '@support/shared/interfaces/select-item.interface';

// services
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';

// services
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { UserService } from '@support/shared/services/user.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { PhonePipe } from '@support/shared/widgets/pipes/phone/phone.pipe';
/**
 * Contains information  on monthly charges on the account landing page
 */
@Component({
  selector: 'tmo-multiline-msisdn-dropdown',
  templateUrl: './multiline-msisdn-dropdown.component.html',
})
export class MultiLineMsisdnDropDownComponent implements OnInit, OnDestroy {
  /**
   * boolean check for single MSISDN Customer
   */
  public isSingleLine: boolean;
  /**
   * boolean check for Multi Line Customer
   */
  @Output() public isMsisdnUpdated: EventEmitter<LineSummary>;
  /**
   * Check if the multi-drop down is of line-landing page or not
   */
  @Input() public isLineDetailsPage: boolean;
  /**
   * List of Lines to be shown as drop down
   */
  public options: SelectItem[];
  /**
   * Stores bill block details
   */
  public customerAccountBillingDetails: CustomerAccountBillingDetails;
  /**
   * Stores Line details for the current line
   */
  public lineDetails: LineSummary;

  /**
   * Used to emit initial selected Msisdn line details if required
   */
  @Input() public initialSelectedMsisdn?: boolean;
  /**
   * stores msisdn in phone format
   */
  public formattedMsisdn: string;
  /**
   * Unsubscriber for all subscribtions
   */
  private ngUnsubscribe: Subject<void>;
  constructor(
    private objectService: ObjectService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private userService: UserService,
    protected phonePipe: PhonePipe,
    private commonUtilService: CommonUtilService
  ) {
    this.ngUnsubscribe = new Subject<void>();
    this.isMsisdnUpdated = new EventEmitter<LineSummary>();
  }

  /**
   * Loads the initial data for Multiline MSISDN
   * @returns void
   */
  public ngOnInit(): void {
    this.loadMultiLineMsisdnOptions();
  }

  /**
   * Unsubscribes for all subscriptions
   * @returns void
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  /**
   * Creates are dropdown for list of all lines in Current Account
   * @param lines {LineSummary[]}
   * @return void
   */
  private populateLineMsisdn(lines: LineSummary[]): void {
    this.options = [];
    this.isSingleLine = lines.length <= 1;
    const msisdn = this.stateStoreUtilsService.getMsisdn();
    if (msisdn) {
      this.formattedMsisdn = this.phonePipe.transform(msisdn);
      lines.forEach((line: LineSummary) => {
        const formattedMsisdn = this.phonePipe.transform(line.msisdn);
        if (formattedMsisdn === this.formattedMsisdn) {
          this.options.push({ label: formattedMsisdn, value: line });
        }
      });
      lines.forEach((line: LineSummary) => {
        const formattedMsisdn = this.phonePipe.transform(line.msisdn);
        if (formattedMsisdn !== this.formattedMsisdn) {
          this.options.push({ label: formattedMsisdn, value: line });
        }
      });
    } else {
      this.options = lines.map((line: LineSummary) => {
        const formattedMsisdn = this.phonePipe.transform(line.msisdn);
        return { label: formattedMsisdn, value: line };
      });
    }
    if (this.initialSelectedMsisdn) {
      this.onSelectionChange({ val: this.objectService.get(this.options, '0.value', {}) });
    }
  }

  /**
   * Loads the bill block details
   * @returns void
   */
  private loadMultiLineMsisdnOptions(): void {
    this.userService
      .getCustomerAccountBillingSummary()
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((response: CustomerAccountBillingDetails) => {
        this.customerAccountBillingDetails = response;
        if (this.commonUtilService.isNotEmpty(response)) {
          const lines = this.objectService.get(response, 'lines');
          if (this.commonUtilService.isNotEmpty(lines)) {
            this.populateLineMsisdn(lines);
            this.isSingleLine = lines.length <= 1;
          }
        }
      });
  }

  /**
   * selection change is used to set selected line details
   * @param {{val: LineSummary}} selectedLine
   */
  public onSelectionChange(selectedLine: { val: LineSummary }): void {
    this.lineDetails = selectedLine.val;
    if (this.isLineDetailsPage) {
      this.stateStoreUtilsService.setMsisdn(this.lineDetails.msisdn);
      this.stateStoreUtilsService.setLineId(this.lineDetails.lineOfServiceId);
    }
    this.isMsisdnUpdated.emit(this.lineDetails);
    this.userService.multilineMsisdnSelected.next(this.lineDetails);
  }
}
